import Modal from '@src/components/common/Modal';
import { type CSSProperties, useState } from 'react';
import styled from 'styled-components';

import {
  FormActions,
  GhostButton,
  PrimaryButton,
  TextInput,
} from './formControls';

import type { TranslationMemoryCollectionInput } from '@src/repositories/TranslationMemoryCollectionRepository';

interface Props {
  mode: 'create' | 'edit';
  initialName?: string;
  initialDescription?: string;
  onClose: () => void;
  /**
   * Persist the collection. Resolve `null` on success (the modal closes) or an
   * error message to show inline (the modal stays open so the user can retry).
   */
  onSubmit: (input: TranslationMemoryCollectionInput) => Promise<string | null>;
}

const CONTENT_STYLE: CSSProperties = {
  width: 'min(440px, 92vw)',
  background: 'var(--surface)',
  borderRadius: 'var(--radius-modal, 12px)',
  padding: '24px',
};

/**
 * Create / rename a translation-memory collection in a modal (name + optional
 * description), matching the entry form's modal pattern (PLAT-10845). Owns the
 * field state, trim/submit, busy guard, and inline error surfacing so a failed
 * write isn't silent.
 */
const CollectionFormModal = ({
  mode,
  initialName = '',
  initialDescription = '',
  onClose,
  onSubmit,
}: Props) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    const message = await onSubmit({
      name: trimmed,
      description: description.trim(),
    });
    setBusy(false);
    if (message) setError(message);
    else onClose();
  };

  return (
    <Modal isOpen closeModal={onClose} contentStyle={CONTENT_STYLE}>
      <Form
        onSubmit={e => {
          e.preventDefault();
          void submit();
        }}
      >
        <h2>{mode === 'create' ? 'New collection' : 'Rename collection'}</h2>

        <Field>
          <label htmlFor="tm-collection-name">Name</label>
          <TextInput
            id="tm-collection-name"
            autoFocus
            value={name}
            placeholder="Collection name"
            onChange={e => setName(e.target.value)}
          />
        </Field>

        <Field>
          <label htmlFor="tm-collection-description">Description</label>
          <TextInput
            id="tm-collection-description"
            value={description}
            placeholder="Description (optional)"
            onChange={e => setDescription(e.target.value)}
          />
        </Field>

        {error && <ErrorText role="alert">{error}</ErrorText>}

        <FormActions>
          <GhostButton type="button" onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton type="submit" disabled={!name.trim() || busy}>
            {mode === 'create' ? 'Add' : 'Save'}
          </PrimaryButton>
        </FormActions>
      </Form>
    </Modal>
  );
};

export default CollectionFormModal;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;

  > h2 {
    font-size: var(--text-lg);
    font-weight: var(--font-weight-bold);
    color: var(--foreground);
    margin: 0 0 2px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  > label {
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
  }
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  color: var(--destructive);
`;
